import type * as React from 'react';
import { type SubmitHandler, useController, useForm } from 'react-hook-form';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { Value } from '@gitbook/slate';
import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldControllerProvider,
    FieldError,
    Fieldset,
    Form,
    Heading,
    Label,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';
import { getChangeRequestSubject } from '@gitbook/util-format';

import { useSetAnalyticsScreen } from '../../navigation';
import { apiClient } from '../../state';
import { encodeDocumentForAPI, RichTextarea, RichTextareaField } from '../BasicEditor';
import { useChangeRequestDescriptionValue } from './ChangeRequestDescriptionField';
import {
    type ChangeRequestPendingRequestedReviewer,
    ChangeRequestReviewersList,
} from './ChangeRequestReviewersList';

type ChangeRequestReviewRequestDialogProps = {
    changeRequest: api.ChangeRequest;
    space: api.Space;
};

type ChangeRequestReviewRequestFormInputs = {
    subject: string;
    description: Value;
    reviewers: ChangeRequestPendingRequestedReviewer[];
};

/**
 * A dialog to request a review on a change request.
 *
 * For now users can only request review from individual users,
 * later we'll add support for requesting reviews from teams.
 */
export function ChangeRequestReviewRequestDialog(
    props: ChangeRequestReviewRequestDialogProps
): React.ReactElement {
    return (
        <Dialog>
            <ChangeRequestReviewRequestDialogContent {...props} />
        </Dialog>
    );
}

function ChangeRequestReviewRequestDialogContent(
    props: ChangeRequestReviewRequestDialogProps
): React.ReactElement {
    const { changeRequest, space } = props;

    const [isPending, startDialogAction] = useDialogAction();

    // If CR already exists, suggest its existing subject.
    const subjectSuggestion = getChangeRequestSubject(changeRequest);
    const form = useForm<ChangeRequestReviewRequestFormInputs>({
        values: {
            subject: changeRequest.subject || subjectSuggestion,
            description: useChangeRequestDescriptionValue(changeRequest),
            reviewers: [],
        },
        disabled: isPending,
    });

    const reviewersController = useController({
        control: form.control,
        name: 'reviewers',
        rules: { required: false },
    });

    useSetAnalyticsScreen(
        analytics.createChangeRequestEvent(space, changeRequest.id, {
            screen: 'space_change_request_request_review',
        }),
        [space.id, changeRequest.id]
    );

    const onSubmit: SubmitHandler<ChangeRequestReviewRequestFormInputs> = async ({
        subject,
        description,
        reviewers,
    }) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const response =
                    await apiClient.changeRequestReviewers.requestReviewersForChangeRequest(
                        space.id,
                        changeRequest.id,
                        {
                            users: reviewers.map((tag) => tag.userId),
                            subject,
                            description: encodeDocumentForAPI(description.document),
                        }
                    );

                const hasReviewers = !!response.data.users?.length;

                toast('Change request submitted', {
                    icon: Icon.ChangeRequestOpen,
                    description: hasReviewers
                        ? 'This change request can now be reviewed and merged.'
                        : "This change request is now ready for a review, but we couldn't find any reviewers to notify.",
                });

                await close();
            }, 'Error while submitting change request for review');
        });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
                <Heading>Request review</Heading>
                <Description>
                    Mark your change request as ready for review and notify team members.
                </Description>
            </DialogHeader>
            <DialogBody>
                <Fieldset>
                    <TextField
                        name="subject"
                        minLength={api.CHANGE_REQUEST_SUBJECT_MIN_LENGTH}
                        maxLength={api.CHANGE_REQUEST_SUBJECT_MAX_LENGTH}
                    >
                        <Label>Add a title</Label>
                        <TextInput autoFocus placeholder={subjectSuggestion} disabled={isPending} />
                        <Description>
                            e.g. 'Adding a Welcome page', 'Updating API intro text'
                        </Description>
                        <FieldError />
                    </TextField>
                    <RichTextareaField name="description" required={false} disabled={isPending}>
                        <Label>Describe your changes</Label>
                        <RichTextarea
                            placeholder="Add your description here..."
                            minRows={4}
                            withEmojis
                            withPageMentions
                        />
                    </RichTextareaField>
                    <FieldControllerProvider controller={reviewersController}>
                        <ChangeRequestReviewersList
                            changeRequest={changeRequest}
                            space={space}
                            pendingRequestedReviewers={reviewersController.field.value}
                            onPendingRequestedReviewersChange={(value) => {
                                reviewersController.field.onChange(value);
                            }}
                            placeholder="Select specific reviewers, or keep it empty to notify all reviewers in your organization."
                        />
                    </FieldControllerProvider>
                </Fieldset>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Request review" disabled={false} />
            </DialogFooter>
        </Form>
    );
}
